import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Tab,
  Tabs,
  CircularProgress,
  Divider
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import EmailOtpLogin from '../components/EmailOtpLogin';
import GoogleSignIn from '../components/GoogleSignIn';

interface LoginFormData {
  username: string;
  password: string;
  twoFactorCode: string | undefined;
}

interface RegisterFormData {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
}

const loginSchema = yup.object({
  username: yup.string().required('Username is required'),
  password: yup.string().required('Password is required'),
  twoFactorCode: yup.string().optional()
});

const registerSchema = yup.object({
  email: yup.string().email('Invalid email').required('Email is required'),
  password: yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
  confirmPassword: yup.string().oneOf([yup.ref('password')], 'Passwords must match').required('Confirm password is required'),
  firstName: yup.string().required('First name is required').max(50, 'First name must be less than 50 characters'),
  lastName: yup.string().required('Last name is required').max(50, 'Last name must be less than 50 characters')
});

const Login: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [needsTwoFactor, setNeedsTwoFactor] = useState(false);
  const { login, register, googleAuth, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  // Redirect if already authenticated
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  const loginForm = useForm({
    resolver: yupResolver(loginSchema)
  });

  const registerForm = useForm<RegisterFormData>({
    resolver: yupResolver(registerSchema)
  });

  const handleLogin = async (data: { username: string; password: string; twoFactorCode?: string }) => {
    setLoading(true);
    setError('');
    try {
      await login(data.username, data.password, data.twoFactorCode);
      navigate('/');
    } catch (err: any) {
      if (err.message.includes('2FA')) {
        setNeedsTwoFactor(true);
        setError('Please enter your 2FA code');
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (data: RegisterFormData) => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await register(data.email, data.password, data.confirmPassword, data.firstName, data.lastName);
      setSuccess('Registration successful! Please check your email to verify your account.');
      registerForm.reset();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (idToken: string) => {
    setLoading(true);
    setError('');
    try {
      const result = await googleAuth(idToken);
      if (result.isNewUser) {
        setSuccess('Account created successfully! Welcome!');
      }
      navigate('/');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = (error: string) => {
    setError(error);
  };

  const handleOtpSuccess = () => {
    navigate('/');
  };

  const handleOtpError = (error: string) => {
    setError(error);
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Typography component="h1" variant="h4" align="center" gutterBottom>
            Login Page
          </Typography>
          
          <Tabs value={tab} onChange={(_, newValue) => setTab(newValue)} centered>
            <Tab label="Login" />
            {/* <Tab label="Email OTP" /> */}
            {/* <Tab label="Google" /> */}
            {/* <Tab label="Register" /> */}
          </Tabs>

          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}

          {tab === 0 && (
            <Box component="form" onSubmit={loginForm.handleSubmit(handleLogin)} sx={{ mt: 3 }}>
              <TextField
                {...loginForm.register('username')}
                fullWidth
                label="Username"
                type="text"
                margin="normal"
                error={!!loginForm.formState.errors.username}
                helperText={loginForm.formState.errors.username?.message}
              />
              <TextField
                {...loginForm.register('password')}
                fullWidth
                label="Password"
                type="password"
                margin="normal"
                error={!!loginForm.formState.errors.password}
                helperText={loginForm.formState.errors.password?.message}
              />
              {needsTwoFactor && (
                <TextField
                  {...loginForm.register('twoFactorCode')}
                  fullWidth
                  label="2FA Code"
                  margin="normal"
                  error={!!loginForm.formState.errors.twoFactorCode}
                  helperText={loginForm.formState.errors.twoFactorCode?.message}
                />
              )}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Login'}
              </Button>
              
              {/* <Divider sx={{ my: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  OR
                </Typography>
              </Divider>
              
              <GoogleSignIn 
                onSuccess={handleGoogleSuccess} 
                onError={handleGoogleError} 
                disabled={loading}
              /> */}
            </Box>
          )}

          {/* {tab === 1 && (
            <Box sx={{ mt: 3 }}>
              <EmailOtpLogin onSuccess={handleOtpSuccess} onError={handleOtpError} />
            </Box>
          )}

          {tab === 2 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom align="center">
                Sign in with Google
              </Typography>
              <GoogleSignIn 
                onSuccess={handleGoogleSuccess} 
                onError={handleGoogleError} 
                disabled={loading}
              />
              <Divider sx={{ my: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  OR
                </Typography>
              </Divider>
              <Typography variant="body2" align="center" color="text.secondary">
                Use the other tabs above for email/password or OTP login
              </Typography>
            </Box>
          )}

          {tab === 3 && (
            <Box component="form" onSubmit={registerForm.handleSubmit(handleRegister)} sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  {...registerForm.register('firstName')}
                  fullWidth
                  label="First Name"
                  margin="normal"
                  error={!!registerForm.formState.errors.firstName}
                  helperText={registerForm.formState.errors.firstName?.message}
                />
                <TextField
                  {...registerForm.register('lastName')}
                  fullWidth
                  label="Last Name"
                  margin="normal"
                  error={!!registerForm.formState.errors.lastName}
                  helperText={registerForm.formState.errors.lastName?.message}
                />
              </Box>
              <TextField
                {...registerForm.register('email')}
                fullWidth
                label="Email"
                type="email"
                margin="normal"
                error={!!registerForm.formState.errors.email}
                helperText={registerForm.formState.errors.email?.message}
              />
              <TextField
                {...registerForm.register('password')}
                fullWidth
                label="Password"
                type="password"
                margin="normal"
                error={!!registerForm.formState.errors.password}
                helperText={registerForm.formState.errors.password?.message}
              />
              <TextField
                {...registerForm.register('confirmPassword')}
                fullWidth
                label="Confirm Password"
                type="password"
                margin="normal"
                error={!!registerForm.formState.errors.confirmPassword}
                helperText={registerForm.formState.errors.confirmPassword?.message}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Register'}
              </Button>
            </Box>
          )} */}
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;